import { useRef } from "react";
import type { StyleSpecification } from "maplibre-gl";
import { buildMainColors, MainColor } from "../../libs/simple/main";
import { structureSignature } from "../../libs/simple/model";

type MapStyle = StyleSpecification & { id: string };

type Cache = { key: string; seen: MapStyle; mains: MainColor[] };

// Keeps the main colours stable while the user only changes colour values,
// so each swatch keeps showing the same shade of its family. Rebuilds when
// the layers change, and whenever a style arrives that the session did not
// produce itself (then from scratch): another style opened (also one with the same id and
// layers, such as a second copy of a template), an undo to before the
// session's first change, an edit made elsewhere.
export function useMainColors(
  style: MapStyle,
  session: { owns(style: MapStyle): boolean }
): MainColor[] {
  const cache = useRef<Cache | null>(null);
  const key = `${style.id || ""}|${structureSignature(style)}`;
  const current = cache.current;

  const foreign =
    current !== null && style !== current.seen && !session.owns(style);

  if (!current || current.key !== key || foreign) {
    // Our own structural change (a zoom split) keeps every family and the
    // shade its swatch shows. Another style starts from the rules.
    const sameStyle =
      current !== null && !foreign && current.seen.id === style.id;
    cache.current = {
      key,
      seen: style,
      mains: buildMainColors(style, sameStyle ? current.mains : undefined),
    };
  } else {
    current.seen = style;
  }
  return cache.current!.mains;
}
